import type { LayerKey, WorkspaceKey } from '@festina-life/api'
import { Button } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useState } from 'react'

import { asFailedSubmit } from './draftCreationStatusReporter'
import { NewDraftPicker } from './NewDraftPicker'
import type {
  DraftCreationModalInitialValue,
  DraftIdentifier,
  DraftSubmitStatus,
} from './types'

type Props = {
  isOpen: boolean
  onSuccess: (
    createdDraft: DraftIdentifier,
    onWorkspaceKey: WorkspaceKey,
  ) => void | Promise<void>
  onCancel: () => void
  initialValue: DraftCreationModalInitialValue
  layer: LayerKey
}

export const DraftCreationFormId = 'DraftCreationForm'

export const DraftCreationModal = ({
  isOpen,
  onSuccess,
  onCancel,
  initialValue,
  layer,
}: Props) => {
  const translate = useTranslate()
  const [submitStatus, setSubmitStatus] = useState<DraftSubmitStatus>()

  // The forms report what they created, and the errors they threw, rather than
  // acting on either themselves.
  const handleStatusChanged = async (status: DraftSubmitStatus) => {
    setSubmitStatus(status)

    if (status.status !== 'success') {
      return
    }

    try {
      await onSuccess(status.createdDraft, status.addedToWorkspaceKey)
    } catch (error) {
      setSubmitStatus(asFailedSubmit(error))
    }
  }

  const handleCancel = () => {
    setSubmitStatus(undefined)
    onCancel()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} panelClassName="max-w-2xl">
      <Modal.Title title={translate('draftModal.title')} />
      <Modal.Body outerClassName="h-175 max-h-175 w-xl max-w-xl overflow-y-hidden!">
        {submitStatus?.status === 'error' && (
          <Alert
            severity="error"
            title={translate('draftModal.submitError.title')}
            onClose={() => setSubmitStatus(undefined)}
          >
            {submitStatus.error.message}
          </Alert>
        )}
        <NewDraftPicker
          onStatusChanged={handleStatusChanged}
          initialValue={initialValue}
          layer={layer}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="discreet" onClick={handleCancel}>
          {translate('close')}
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={DraftCreationFormId}
          isLoading={submitStatus?.status === 'submitting'}
        >
          {translate('apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
